// @flow
// import-sort-ignore

import {
  capitalize,
  find,
  flatten,
  flow,
  get,
  isNumber,
  max,
  reduce,
} from 'lodash';

import {
  type ChangeType,
  changeTypes,
  type Interval,
  intervalTypes,
  type Plan,
  type PlanIntervalProps,
  type PlanTier,
  type QuestionProps,
  relativeRound,
} from '@zapier/common-utils';

// eslint-disable-next-line @zapier/zapier/disallow-incorrect-import-notation
import { isMonthly, isYearly } from '@zapier/billing/src/utils';

import { UPGRADE_BASE_URL } from 'app/billing/constants';
import { getCurrentPathname } from 'app/common/utils/RouterSelectors';
import { clamp, infinitize } from 'app/common/CommonUtils';

import { type PriceFormatOptions } from 'app/billing/types';

/* eslint-enable @zapier/zapier/disallow-incorrect-import-notation */

export const formatPrice = (
  price: number,
  {
    forceDecimalPoints = false,
    maximumFractionDigits = 2,
    relativeRounding = false,
  }: PriceFormatOptions = {}
): string => {
  // Price is given in pennies so convert to dollars
  const priceInDollars = relativeRounding
    ? relativeRound(price) / 100
    : price / 100;
  const hasPennies = priceInDollars % 1 !== 0;

  if (hasPennies || forceDecimalPoints) {
    return priceInDollars.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits,
    });
  }

  return priceInDollars.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

export const formatPriceWithCurrencySymbol = (
  price: number,
  formattingOptions?: PriceFormatOptions,
  symbol: string = '$'
): string =>
  price >= 0
    ? `${symbol}${formatPrice(price, formattingOptions)}`
    : `–${symbol}${formatPrice(Math.abs(price), formattingOptions)}`;

export const formatMeterizedAmount = (amount: number): string =>
  `Starts at ${formatPriceWithCurrencySymbol(amount)}`;

export const formatPlanAmount = (
  isPlanMetered: boolean,
  amount: number
): string =>
  (isPlanMetered ? formatMeterizedAmount : formatPriceWithCurrencySymbol)(
    amount
  );

export const getAmountCopy = (billingInterval: string): string => {
  return isYearly(billingInterval) ? `Per Mo / Billed Annually` : 'Per Month';
};

export const getPageSize = (queries: {}): number => {
  if (!queries) return 0;
  const matches = Object.keys(queries).filter(k => queries[k]);
  const sizes = matches.map(m => m.match(/\d+/)[0]).map(s => parseInt(s));
  return sizes.length ? max(sizes) : 1;
};

export const calculateDiscount = (
  yearlyAmount: number,
  monthlyAmount: number
): number => {
  const total = monthlyAmount * 12;
  const discount = total - yearlyAmount;
  return Math.floor((discount / total) * 100);
};

export const getYearlyDiscount = (plan: Plan) => {
  if (!plan) return 0;
  const yearlyInfo = find(plan.intervals, i => isYearly(i.interval));
  const monthlyInfo = find(plan.intervals, i => isMonthly(i.interval));
  if (!yearlyInfo || !monthlyInfo) {
    return 0;
  }

  return calculateDiscount(yearlyInfo.amount, monthlyInfo.amount);
};

const getOneEnticement = (plan: Plan, interval: Interval): string => {
  if (!isYearly(interval)) {
    return '';
  }

  const yearlyInfo = find(plan.intervals, i => isYearly(i.interval));
  const monthlyInfo = find(plan.intervals, i => isMonthly(i.interval));
  if (!yearlyInfo || !monthlyInfo) {
    return '';
  }

  if (monthlyInfo.amount * 11 === yearlyInfo.amount) {
    return 'free month';
  }

  const discount = calculateDiscount(yearlyInfo.amount, monthlyInfo.amount);
  return `save ${discount}%`;
};

export const getEnticements = (plan: Plan) => {
  return plan.intervals.reduce((enticements, interval) => {
    enticements[interval.interval] = getOneEnticement(plan, interval.interval);
    return enticements;
  }, {});
};

const oneCardWidth = (pageSize: number): number => 100 / pageSize;

export const getMinOffset = (
  numCards: number = 0,
  pageSize: number = 0
): number => {
  const offset = (numCards - pageSize) * oneCardWidth(pageSize) * -1;
  return Math.min(offset, 0);
};

export const getInitialOffset = (
  numCards: number = 0,
  pageSize: number = 0,
  currentPlanIndex: number = 0
) => {
  if (!isNumber(currentPlanIndex) || currentPlanIndex < 0) {
    return 0;
  }
  const minOffset = getMinOffset(numCards, pageSize);
  const offset = (currentPlanIndex + 1) * oneCardWidth(pageSize) * -1;
  return Math.max(offset, minOffset);
};

export const getOffset = (
  currentOffset: number = 0,
  pageSize: number = 0,
  numCards: number = 0,
  direction: string = ''
) => {
  const minOffset = getMinOffset(numCards, pageSize);
  const offset =
    direction === 'forward' ? currentOffset - 100 : currentOffset + 100;
  return Math.min(Math.max(minOffset, offset), 0);
};

export const isFirstCardsPage = (offset?: number = 0): boolean => offset === 0;

export const isLastCardsPage = (
  offset?: number = 0,
  numCards: number,
  pageSize: number
): boolean => {
  const minOffset = getMinOffset(numCards, pageSize);
  return offset <= minOffset;
};

export const getCurrentBillingInterval = (
  plans: Array<Plan>,
  currentPlanId: number
): ?PlanIntervalProps => {
  const allIntervals = flatten(plans.map(p => p.intervals));
  return find(allIntervals, { id: currentPlanId });
};

export const getDefaultBillingInterval = (
  currentPlan: Plan,
  currentBillingInterval: PlanIntervalProps
) =>
  currentPlan.billingAmount === 0
    ? intervalTypes.year
    : currentBillingInterval.interval;

/*
   The server expects a very specific format here. Ie:

   'Too expensive\nLost my job\nThis is a comment'

   Or, if no why (no questions checked):

   'This is a comment'

   (no newline before the comment)
*/
export const formatPlanDowngradeWhy = (
  questions: Array<QuestionProps>,
  comment?: string = ''
): string => {
  // include '\n' on every why line including the last line.
  const why = questions
    .filter(q => q.isChecked)
    .map(q => `Checked: ${q.question}\n`)
    .join('');

  // If there are no checked questions, and no why, do not put a '\n'
  return `${why}Comments: ${comment || ''}`;
};

export const shouldLoad = (
  isLoading: boolean,
  hasMounted: boolean
): boolean => {
  if (hasMounted) {
    return false;
  }
  if (isLoading) {
    return true;
  }
  return false;
};

export const getBillingInterval = (
  selectedBillingIntervals: {},
  plan: Plan
) => {
  if (!plan) {
    return '';
  }
  // $FlowFixMe
  const interval = find(
    selectedBillingIntervals,
    (value, key) => key.toLowerCase() === plan.name.toLowerCase()
  );
  if (interval) {
    return interval;
  }

  // default to 'year' if not found, unless it's a plan that does not have 'year' interval
  const planIntervalNames = plan.intervals
    .map(i => i.interval)
    .map(n => n.toLowerCase());
  return planIntervalNames.indexOf('year') === -1
    ? planIntervalNames[0]
    : 'year';
};

export const getChangeLabel = (changeType: ChangeType): string => {
  switch (changeType) {
    case changeTypes.downgrade:
      return 'Downgrade';
    case changeTypes.upgrade:
      return 'Upgrade';
    case changeTypes.change:
      return 'Update';
  }

  return capitalize(changeType);
};

export const getChangeLabelForPlanName = (
  changeType: ChangeType,
  planName: string
): string => {
  const changeLabel = getChangeLabel(changeType);

  if (changeType === changeTypes.change) {
    return `${changeLabel} Billing for ${planName} Plan`;
  }

  return `${changeLabel} to ${planName} Plan`;
};

// Calculates how much `numTasksUsed` would cost based on the
// price per task and number of tasks within each of the `tiers`.
export const calcMeteredPrice = (
  tiers: Array<PlanTier>,
  numTasksUsed: number
): number => {
  const sumTasksInLowerTiers = i =>
    tiers
      .slice(0, i)
      .reduce((accumTasks, { numTasks }) => accumTasks + numTasks, 0);
  return tiers.reduce((cost, { numTasks, pricePerTask }, i) => {
    const numTasksInLowerTiers = sumTasksInLowerTiers(i);
    const numTasksInThisTier = clamp(
      0,
      infinitize(numTasks),
      numTasksUsed - numTasksInLowerTiers
    );
    return cost + pricePerTask * numTasksInThisTier;
  }, 0);
};

export const buildDowngradeTeamWarningUrl = (
  fromPlanName: string,
  toPlanName: string,
  interval: string
): string =>
  encodeURI(
    `/app/billing/plans/downgrade-team/${fromPlanName}/${toPlanName}/${interval}`
  );

export const buildDowngradeWarningUrl = (
  fromPlanName: string,
  toPlanName: string,
  interval: string
): string =>
  encodeURI(
    `/app/billing/plans/downgrade-plan/${fromPlanName}/${toPlanName}/${interval}`
  );

export const buildPlanChangeUrl = (
  changeType: string,
  fromPlanName: string,
  toPlanName?: string = '',
  selectedInterval?: string
): string => {
  if (selectedInterval) {
    return encodeURI(
      `/app/billing/plans/${changeType}/${fromPlanName}/${toPlanName}?interval=${selectedInterval}`
    );
  }

  if (changeType === 'change') {
    return encodeURI(`/app/billing/plans/${changeType}/${fromPlanName}`);
  }

  return encodeURI(
    `/app/billing/plans/${changeType}/${fromPlanName}/${toPlanName}`
  );
};

export const buildDowngradeOfferUrl = (
  fromPlanName: string,
  toPlanName: string,
  interval: string,
  offerCode: string
): string =>
  encodeURI(
    `/app/billing/plans/downgrade-offer/${offerCode}/${fromPlanName}/${toPlanName}/${interval}`
  );

const getDataSet = node => get(node, 'dataset', {});

const transformDataSet = dataset => {
  // wish I could do this in a cleaner way, but it seems to hate Object.assign,
  // the spread operator and _.values in Safari, so good ol' fashioned reduce
  // will have to do the trick.
  return reduce(
    dataset,
    (newObj, value, key) => {
      newObj[key] = value;
      return newObj;
    },
    {}
  );
};

export const mapTrialExtensionModalToProps = flow([
  getDataSet,
  transformDataSet,
  props => ({
    ...props,
    errorCode: Number(props.errorCode),
  }),
]);

export const isOnUpgradeRoute = (state: Object) => {
  return getCurrentPathname(state).includes(UPGRADE_BASE_URL);
};
